import { ComponentType, lazy } from 'react';
import { systemLogger } from './logging';

interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  backoffMultiplier?: number;
}

/**
 * Detect if an error is a chunk loading failure
 * These typically occur during deployments when old chunk files are deleted
 */
function isChunkLoadError(error: unknown): boolean {
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Importing a module script failed') ||
    errorMessage.includes('error loading dynamically imported module') ||
    errorMessage.includes('ChunkLoadError')
  );
}

/**
 * Wait for specified milliseconds
 */
function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Enhanced lazy loading with retry logic and smart error handling
 *
 * Handles two scenarios:
 * 1. Network glitches: Retries with exponential backoff
 * 2. Deployment race conditions: Auto-reloads page after retries exhausted
 *
 * @example
 * const ProjectPage = lazyWithRetry(() => import('./ProjectPage'));
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: RetryOptions = {}
) {
  const {
    maxRetries = 3,
    delay = 1000,
    backoffMultiplier = 2
  } = options;

  return lazy(async () => {
    let lastError: unknown = null;
    let currentDelay = delay;

    // Try to load the module with retries
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        systemLogger.debug('lazyWithRetry', `Attempt ${attempt}/${maxRetries} to load module`);
        const module = await importFn();

        if (attempt > 1) {
          systemLogger.info('lazyWithRetry', `Module loaded successfully after ${attempt} attempts`);
        }

        return module;
      } catch (error) {
        lastError = error;
        const isChunkError = isChunkLoadError(error);

        systemLogger.warn('lazyWithRetry', `Attempt ${attempt}/${maxRetries} failed`, {
          error,
          isChunkError,
          willRetry: attempt < maxRetries
        });

        // If not the last attempt, wait before retrying
        if (attempt < maxRetries) {
          await wait(currentDelay);
          currentDelay *= backoffMultiplier;
        }
      }
    }

    // All retries exhausted - check if it's a chunk loading error
    if (isChunkLoadError(lastError)) {
      systemLogger.error(
        'lazyWithRetry',
        'Chunk loading failed after all retries - likely deployment race condition',
        { error: lastError }
      );

      // Show user notification before reload
      window.confirm(
        'A new version of the application is available. The page will reload to load the latest version.'
      );

      // Auto-reload to fetch new chunks
      systemLogger.info('lazyWithRetry', 'Reloading page to fetch new chunks');
      window.location.reload();

      // Throw error to prevent further rendering while page reloads
      throw new Error('Chunk loading failed - reloading application');
    }

    // Not a chunk error - throw original error for normal error handling
    systemLogger.error('lazyWithRetry', 'Module loading failed after all retries', {
      error: lastError
    });
    throw lastError;
  });
}

/**
 * Check if an error should trigger a page reload
 * Exported for use in error boundaries
 */
export function shouldReloadOnError(error: unknown): boolean {
  return isChunkLoadError(error);
}
